import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import ErrorBoundary from './ErrorBoundary';
import FleetLayout from './layout/FleetLayout';
import ProtectedRoute from './routes/ProtectedRoute';
import RequireAbility from './routes/RequireAbility';
import AppLoadingScreen from '@/shared/ui/AppLoadingScreen';
import LoginPage from '@/pages/auth/LoginPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import FleetLandingPage from '@/pages/marketing/FleetLandingPage';

const FleetNeedsFormPage = lazy(() => import('@/pages/marketing/FleetNeedsFormPage'));
const DriverDetail = lazy(() => import('@/pages/Drivers/DriverDetailPage'));
const DriverList = lazy(() => import('@/pages/Drivers/DriversListPage'));
const FineDetailPage = lazy(() => import('@/pages/Fines/FineDetailPage'));
const FinesListPage = lazy(() => import('@/pages/Fines/FinesListPage'));
const FuelCardDetailPage = lazy(() => import('@/pages/FuelCards/FuelCardDetailPage'));
const FuelCardsPage = lazy(() => import('@/pages/FuelCards/FuelCardsPage'));
const RedirectToCurrentFuelMonth = lazy(
  () => import('@/pages/fleet-expenses/RedirectToCurrentFuelMonth')
);
const FuelMonthDetailPage = lazy(() => import('@/pages/fleet-expenses/FuelMonthDetailPage'));
const MaintenancePage = lazy(() => import('@/pages/Maintenance/MaintenancePage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const OperationsRoomPage = lazy(() => import('@/pages/operations/OperationsRoomPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const UsersPage = lazy(() => import('@/pages/users/UsersPage'));
const TenantsPage = lazy(() => import('@/pages/tenants/TenantsPage'));
const TenantDetailPage = lazy(() => import('@/pages/tenants/TenantDetailPage'));
const DevicesPage = lazy(() => import('@/pages/devices/DevicesPage'));
const VehicleDetail = lazy(() => import('@/pages/Vehicles/VehicleDetailPage'));
const VehicleList = lazy(() => import('@/pages/Vehicles/VehicleList'));
const WorkshopsPage = lazy(() => import('@/pages/workshops/WorkshopsPage'));

function PageLoadingFallback() {
  return <AppLoadingScreen message="Caricamento pagina…" />;
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route path="/" element={<FleetLandingPage />} />
          <Route path="/richiedi-demo" element={<FleetNeedsFormPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<FleetLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/operations" element={<OperationsRoomPage />} />

              <Route path="/vehicles">
                <Route index element={<VehicleList />} />
                <Route path="new" element={<VehicleDetail />} />
                <Route path=":id" element={<VehicleDetail />} />
              </Route>

              <Route path="/drivers">
                <Route index element={<DriverList />} />
                <Route path=":id" element={<DriverDetail />} />
              </Route>

              <Route path="/maintenance">
                <Route index element={<MaintenancePage />} />
              </Route>

              <Route path="/fleet-expenses">
                <Route index element={<RedirectToCurrentFuelMonth />} />
                <Route path="fuel/:monthKey" element={<FuelMonthDetailPage />} />
                <Route path="cards" element={<FuelCardsPage />} />
              </Route>

              <Route path="/fuel-cards/:id" element={<FuelCardDetailPage />} />

              <Route path="/fines">
                <Route index element={<FinesListPage />} />
                <Route path=":id" element={<FineDetailPage />} />
              </Route>

              <Route path="/workshops" element={<WorkshopsPage />} />

              <Route path="/settings" element={<SettingsPage />} />

              <Route element={<RequireAbility I="read" a="User" />}>
                <Route path="/users" element={<UsersPage />} />
              </Route>

              <Route element={<RequireAbility I="read" a="Device" />}>
                <Route path="/devices" element={<DevicesPage />} />
              </Route>

              <Route element={<RequireAbility I="read" a="Tenant" />}>
                <Route path="/tenants">
                  <Route index element={<TenantsPage />} />
                  <Route path=":id" element={<TenantDetailPage />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
